import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Tag, Clock, Sparkles, ArrowRight, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COUPON_STRIPE_LINK = "https://buy.stripe.com/bJe9AT3Ou7wpgW19gK8g001";
const COOKIE_NAME = "smc_exit_offer_shown";
const COOKIE_DAYS = 30;
const COUNTDOWN_SECONDS = 10 * 60; // 10:00

const FALLBACK = {
  message: "Wait! Grab all 11 ebooks + 3 bonuses for just $17 before you go.",
  recommended: [
    "The Ultimate Faceless Marketing Guide",
    "Faceless Youtube Automation For Passive Profits",
    "TikTok Money Guide",
  ],
};

function setCookie(name, value, days) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${d.toUTCString()};path=/;SameSite=Lax`;
}
function getCookie(name) {
  return document.cookie.split("; ").some((c) => c.startsWith(`${name}=`));
}

const isTouchDevice = () =>
  typeof window !== "undefined" &&
  ("ontouchstart" in window || window.matchMedia("(pointer: coarse)").matches);

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export default function ExitIntentPopup() {
  const [open, setOpen] = useState(false);
  const [offer, setOffer] = useState(FALLBACK);
  const [seconds, setSeconds] = useState(COUNTDOWN_SECONDS);

  const triggered = useRef(false);
  const startTime = useRef(Date.now());
  const maxScroll = useRef(0);
  const lastY = useRef(0);

  // --- Trigger detection ---
  useEffect(() => {
    if (getCookie(COOKIE_NAME)) return; // already shown in last 30 days

    const trigger = async () => {
      if (triggered.current) return;
      triggered.current = true;
      setCookie(COOKIE_NAME, "1", COOKIE_DAYS);

      const timeOnPage = Math.round((Date.now() - startTime.current) / 1000);
      if (typeof window.gtag === "function") {
        window.gtag("event", "exit_intent_shown", {
          time_on_page: timeOnPage,
          scroll_depth: maxScroll.current,
        });
      }
      try {
        const res = await fetch(`${API}/exit-offer`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ time_on_page: timeOnPage, scroll_depth: maxScroll.current }),
        });
        if (res.ok) {
          const data = await res.json();
          setOffer({ message: data.message || FALLBACK.message, recommended: data.recommended || FALLBACK.recommended });
        }
      } catch (e) {
        /* keep fallback */
      }
      setOpen(true);
    };

    const onScroll = () => {
      const y = window.scrollY;
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      const depth = docH > 0 ? Math.round((y / docH) * 100) : 0;
      if (depth > maxScroll.current) maxScroll.current = depth;
      // Mobile: user scrolled down meaningfully then rushes back to the top
      if (isTouchDevice() && lastY.current > 400 && y < 90) trigger();
      lastY.current = y;
    };

    const onMouseOut = (e) => {
      // Desktop: cursor leaves through the top of the viewport (toward tabs/close)
      if (!e.relatedTarget && e.clientY <= 0) trigger();
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    if (!isTouchDevice()) document.addEventListener("mouseout", onMouseOut);
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, []);

  // --- Countdown ---
  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => setSeconds((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [open]);

  const claim = () => {
    if (typeof window.gtag === "function") {
      window.gtag("event", "begin_checkout", {
        currency: "USD",
        value: 17.0,
        coupon: "EXIT10",
        items: [{ item_id: "FACELESS-BUNDLE-11", item_name: "The Faceless Marketing Bundle", price: 17.0, quantity: 1 }],
      });
    }
    window.location.href = COUPON_STRIPE_LINK;
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="exit-popup-overlay"
        >
          <div className="absolute inset-0 bg-[#FFF7ED]/80 backdrop-blur-sm" onClick={() => setOpen(false)} />

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 w-full max-w-md bg-[#FFFFFF] border border-[#F59E0B]/40 shadow-[0_0_60px_rgba(212,175,55,0.15)]"
            data-testid="exit-popup"
          >
            {/* gold top bar */}
            <div className="h-1 w-full bg-[#F59E0B]" />

            <button
              onClick={() => setOpen(false)}
              data-testid="exit-popup-close"
              aria-label="Close"
              className="absolute top-4 right-4 text-[#666666] hover:text-[#232323] transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="p-8 text-center">
              <div className="inline-flex items-center gap-2 border border-[#F59E0B]/50 bg-[#F59E0B]/10 px-4 py-1.5 mb-6">
                <Tag className="w-3.5 h-3.5 text-[#F59E0B]" />
                <span className="text-[11px] uppercase tracking-[0.25em] text-[#F59E0B]">Exclusive coupon unlocked</span>
              </div>

              <h2 className="font-serif-display text-3xl sm:text-4xl font-bold leading-tight">
                Wait — <span className="gold-shimmer">Save $10</span>
              </h2>
              <div className="mt-3 flex items-center justify-center gap-3">
                <span className="font-serif-display text-2xl line-through text-[#8A8A8A]">$27</span>
                <span className="font-serif-display text-5xl font-bold text-[#F59E0B]">$17</span>
                <span className="text-xs uppercase tracking-widest text-[#666666] border border-[#E5D8C8] px-2 py-1">Today only</span>
              </div>

              {/* AI personalized message */}
              <p className="mt-6 text-[#353535] leading-relaxed flex items-start gap-2 text-left justify-center">
                <Sparkles className="w-4 h-4 text-[#F59E0B] shrink-0 mt-1" />
                <span data-testid="exit-popup-message">{offer.message}</span>
              </p>

              {/* AI recommended ebooks */}
              <div className="mt-5 text-left" data-testid="exit-popup-recommended">
                <p className="text-[11px] uppercase tracking-widest text-[#666666] mb-2">Recommended for you</p>
                <div className="flex flex-wrap gap-2">
                  {offer.recommended.map((t, i) => (
                    <span key={i} className="text-xs text-[#353535] border border-[#F59E0B]/30 bg-[#F59E0B]/5 px-3 py-1.5">
                      {t}
                    </span>
                  ))}
                </div>
              </div>

              {/* Countdown */}
              <div className="mt-7 flex items-center justify-center gap-2 text-sm text-[#666666]">
                <Clock className="w-4 h-4 text-[#F59E0B]" />
                Offer expires in
                <span className="font-mono font-bold text-[#232323] tabular-nums" data-testid="exit-popup-timer">{fmt(seconds)}</span>
              </div>

              <button onClick={claim} data-testid="exit-popup-claim" className="btn-gold w-full mt-6 py-4 text-sm flex items-center justify-center gap-2">
                Claim $17 Deal Now <ArrowRight className="w-4 h-4" />
              </button>

              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-[#8A8A8A]">
                <ShieldCheck className="w-3.5 h-3.5" /> Secure checkout · 30-day money-back guarantee
              </div>

              <button onClick={() => setOpen(false)} data-testid="exit-popup-dismiss" className="mt-4 text-xs text-[#8A8A8A] hover:text-[#666666] underline transition-colors">
                No thanks, I'll pay full price
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
